import React from "react";
import { useApp } from "../context/AppContext";
import { ROLE_HOME } from "../data/mockData";
import Logo from "../components/Logo";

// Marketing landing page — the entry screen. Self-contained full-screen layout
// (no Sidebar). All CSS is scoped under `.compass-landing` so nothing leaks into
// the rest of the app (no global `*`/`body` resets).
export default function LandingPage() {
  const { nav, currentRole } = useApp();
  const goToDashboard = () => nav(ROLE_HOME[currentRole] || "portfolio");

  return (
    <div className="compass-landing">
      <style>{CSS}</style>

      {/* Top bar */}
      <div className="cl-topbar"><div className="cl-wrap cl-topbar-inner">
        <div className="cl-brand">
          <Logo size={36} />
          <div><b>Compass</b><span>Competitor Intelligence</span></div>
        </div>
        <nav className="cl-nav">
          <a href="#cl-how">How it works</a>
          <a href="#cl-roles">For teams</a>
        </nav>
      </div></div>

      {/* Hero */}
      <div className="cl-hero"><div className="cl-wrap">
        <span className="cl-eyebrow">Leads from every competitor show</span>
        <h1>Their exhibitors.<br />Your pipeline.</h1>
        <p className="cl-sub">Every competitor trade show is a list of buyers already spending in your market. Compass captures those exhibitors, enriches and scores them, and delivers ready-to-work leads straight to your team's desk.</p>
        <div className="cl-cta-row">
          <button className="cl-btn cl-btn-primary" onClick={goToDashboard}>Go to my dashboard</button>
        </div>
        <p className="cl-trust">Your leads are already inside. Pick an event to start.</p>
      </div></div>

      {/* Why strip */}
      <div className="cl-why"><div className="cl-wrap cl-why-grid">
        <div className="cl-col"><b>What</b><p>One home for every competitor exhibitor worth knowing.</p></div>
        <div className="cl-col"><b>How</b><p>Track the show. Compass enriches &amp; scores it. Teams approve the best leads. They flow to CRM &amp; campaigns.</p></div>
        <div className="cl-col"><b>Why</b><p>They're already paying to be in your market. Reach them first.</p></div>
      </div></div>

      {/* How it works */}
      <section className="cl-block cl-dark" id="cl-how"><div className="cl-wrap">
        <div className="cl-kicker">How it works</div>
        <h2 className="cl-sec">From competitor show to qualified pipeline.</h2>
        <p className="cl-secsub">Four steps turn a competitor's exhibitor catalogue into qualified leads for Sales and Marketing.</p>
        <div className="cl-flow">
          <svg viewBox="0 0 960 220" role="img" aria-label="Track, then Enrich, then Qualify, then Act">
            <line className="cl-flowline" x1="120" y1="70" x2="840" y2="70"/>
            <line className="cl-flowpulse" x1="120" y1="70" x2="840" y2="70"/>
            <g>
              <circle className="cl-node" cx="120" cy="70" r="34"/><circle className="cl-dot" cx="120" cy="70" r="7" style={{ animationDelay: "0s" }}/>
              <text className="cl-nlabel" x="120" y="140" textAnchor="middle">Track</text>
              <text className="cl-ndesc" x="120" y="160" textAnchor="middle">Add a competitor show</text>
            </g>
            <g>
              <circle className="cl-node" cx="360" cy="70" r="34"/><circle className="cl-dot" cx="360" cy="70" r="7" style={{ animationDelay: "0.5s" }}/>
              <text className="cl-nlabel" x="360" y="140" textAnchor="middle">Enrich</text>
              <text className="cl-ndesc" x="360" y="158" textAnchor="middle"><tspan x="360" dy="0">Compass profiles &amp; scores</tspan><tspan x="360" dy="14">every exhibitor — automatically</tspan></text>
            </g>
            <g>
              <circle className="cl-node" cx="600" cy="70" r="34"/><circle className="cl-dot" cx="600" cy="70" r="7" style={{ animationDelay: "1s" }}/>
              <text className="cl-nlabel" x="600" y="140" textAnchor="middle">Qualify</text>
              <text className="cl-ndesc" x="600" y="158" textAnchor="middle"><tspan x="600" dy="0">Sales &amp; Marketing review</tspan><tspan x="600" dy="14">and approve the best-fit leads</tspan></text>
            </g>
            <g>
              <circle className="cl-node" cx="840" cy="70" r="34"/><circle className="cl-dot" cx="840" cy="70" r="7" style={{ animationDelay: "1.5s" }}/>
              <text className="cl-nlabel" x="840" y="140" textAnchor="middle">Act</text>
              <text className="cl-ndesc" x="840" y="158" textAnchor="middle"><tspan x="840" dy="0">Approved leads flow to</tspan><tspan x="840" dy="14">your CRM and campaigns</tspan></text>
            </g>
          </svg>
        </div>
      </div></section>

      {/* Roles */}
      <section className="cl-block cl-fork" id="cl-roles"><div className="cl-wrap">
        <div className="cl-kicker">One platform, every seat</div>
        <h2 className="cl-sec">Three teams, one shared source of truth.</h2>
        <p className="cl-secsub">Log in and Compass already knows your job. Same intel — shaped for how you actually work.</p>
        <div className="cl-cards">
          <div className="cl-rcard"><div className="cl-role">Sales</div><h3>A call list that arrives qualified.</h3><p>Review-ready leads from every competitor show, ranked by fit and priority. You'll always know who to call next.</p></div>
          <div className="cl-rcard"><div className="cl-role">Marketing</div><h3>Audiences pulled from live market demand.</h3><p>Target qualified leads by event and category and campaign to companies already spending in your space.</p></div>
          <div className="cl-rcard"><div className="cl-role">Management</div><h3>Proof it's paying off.</h3><p>See which competitor shows generate the strongest pipeline and where demand is growing to guide strategic investment.</p></div>
        </div>
      </div></section>

      {/* Final CTA */}
      <div className="cl-final"><div className="cl-wrap">
        <h2>Your leads are already inside.</h2>
        <p>Open your dashboard and turn competitor shows into pipeline.</p>
        <button className="cl-btn cl-btn-primary" onClick={goToDashboard}>Go to my dashboard</button>
      </div></div>

      <footer className="cl-footer">Compass — internal platform</footer>
    </div>
  );
}

const CSS = `
.compass-landing{
  --soft:#FAFAFA;--panel:#F4F4F5;--green:#E8431A;--green-d:#C23417;
  --bg:#FFFFFF;--muted:#818084;--body:#3F3F46;--dark-text:#26262A;--line:#D8DADC;
  font-family:'Montserrat',sans-serif;color:var(--dark-text);background:var(--bg);
  line-height:1.5;min-height:100vh;overflow-y:auto;
}
.compass-landing *{box-sizing:border-box;margin:0;padding:0}
.compass-landing .cl-wrap{max-width:1140px;margin:0 auto;padding:0 28px}
.compass-landing .cl-btn{display:inline-block;text-decoration:none;font-weight:700;border-radius:10px;padding:14px 26px;font-size:15px;cursor:pointer;transition:.15s;border:none;font-family:inherit}
.compass-landing .cl-btn-sm{padding:9px 18px}
.compass-landing .cl-btn-primary{background:var(--green);color:#fff}
.compass-landing .cl-btn-primary:hover{background:var(--green-d);color:#fff}
.compass-landing .cl-topbar{background:#fff;color:var(--dark-text);padding:16px 0;border-bottom:1px solid var(--line)}
.compass-landing .cl-topbar-inner{display:flex;align-items:center;justify-content:space-between}
.compass-landing .cl-brand{display:flex;align-items:center;gap:12px}
.compass-landing .cl-brand b{font-size:18px;letter-spacing:-.3px}
.compass-landing .cl-brand span{display:block;font-size:11px;color:var(--muted);font-weight:500}
.compass-landing .cl-nav{display:flex;align-items:center}
.compass-landing .cl-nav a{color:var(--body);text-decoration:none;margin-left:26px;font-size:14px;font-weight:600}
.compass-landing .cl-nav a:hover{color:var(--green)}
.compass-landing .cl-nav .cl-btn{margin-left:26px}
.compass-landing .cl-hero{background:var(--soft);color:var(--dark-text);padding:70px 0 90px;text-align:center;position:relative;overflow:hidden;border-bottom:1px solid var(--line)}
.compass-landing .cl-hero:after{content:"";position:absolute;top:-120px;right:-120px;width:420px;height:420px;background:radial-gradient(circle,rgba(232,67,26,.12),transparent 70%)}
.compass-landing .cl-eyebrow{display:inline-block;font-size:12px;letter-spacing:2px;text-transform:uppercase;color:var(--green);font-weight:800;margin-bottom:22px;background:rgba(232,67,26,.1);padding:6px 14px;border-radius:20px}
.compass-landing .cl-hero h1{font-size:62px;line-height:1.03;letter-spacing:-1.5px;font-weight:900;max-width:900px;margin:0 auto 22px;position:relative}
.compass-landing .cl-sub{font-size:20px;color:var(--body);max-width:720px;margin:0 auto 30px;position:relative}
.compass-landing .cl-cta-row{display:flex;gap:14px;justify-content:center;margin-bottom:20px;position:relative}
.compass-landing .cl-trust{font-size:13px;color:var(--muted);position:relative}
.compass-landing .cl-why{background:#fff;color:var(--dark-text);padding:36px 0;border-bottom:1px solid var(--line)}
.compass-landing .cl-why-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:26px}
.compass-landing .cl-col b{color:var(--green);font-size:13px;letter-spacing:1px;text-transform:uppercase;display:block;margin-bottom:6px}
.compass-landing .cl-col p{color:var(--body);font-size:15px}
.compass-landing .cl-block{padding:74px 0}
.compass-landing .cl-dark{background:var(--soft);color:var(--dark-text)}
.compass-landing .cl-kicker{text-align:center;color:var(--green-d);font-weight:800;letter-spacing:1.5px;text-transform:uppercase;font-size:13px;margin-bottom:10px}
.compass-landing .cl-dark .cl-kicker{color:var(--green)}
.compass-landing .cl-sec{text-align:center;font-size:38px;letter-spacing:-.8px;font-weight:900;margin-bottom:12px}
.compass-landing .cl-secsub{text-align:center;color:var(--muted);font-size:17px;max-width:640px;margin:0 auto 46px}
.compass-landing .cl-dark .cl-secsub{color:var(--muted)}
.compass-landing .cl-flow{position:relative;max-width:960px;margin:0 auto}
.compass-landing .cl-flow svg{width:100%;height:auto;display:block}
.compass-landing .cl-flowline{stroke:var(--line);stroke-width:3;fill:none}
.compass-landing .cl-flowpulse{stroke:var(--green);stroke-width:3;fill:none;stroke-linecap:round;stroke-dasharray:16 220;animation:cl-dash 3s linear infinite;filter:drop-shadow(0 0 5px rgba(232,67,26,.5))}
@keyframes cl-dash{to{stroke-dashoffset:-236}}
.compass-landing .cl-node{fill:#fff;stroke:var(--line);stroke-width:2}
.compass-landing .cl-nlabel{fill:var(--dark-text);font-weight:800;font-size:15px}
.compass-landing .cl-ndesc{fill:var(--muted);font-size:11.5px}
.compass-landing .cl-dot{fill:var(--green);animation:cl-blink 2s ease-in-out infinite}
@keyframes cl-blink{0%,100%{opacity:.35}50%{opacity:1}}
@media(prefers-reduced-motion:reduce){
  .compass-landing .cl-flowpulse,.compass-landing .cl-dot{animation:none}
  .compass-landing .cl-flowpulse{stroke-dasharray:none;opacity:.6}
}
.compass-landing .cl-fork{background:var(--panel);color:var(--dark-text)}
.compass-landing .cl-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:22px}
.compass-landing .cl-rcard{background:#fff;border:1px solid var(--line);border-radius:18px;padding:30px}
.compass-landing .cl-rcard .cl-role{color:var(--green);font-weight:800;font-size:13px;letter-spacing:1px;text-transform:uppercase;margin-bottom:12px}
.compass-landing .cl-rcard h3{font-size:23px;margin-bottom:10px;letter-spacing:-.4px}
.compass-landing .cl-rcard p{color:var(--body);font-size:15px}
.compass-landing .cl-fork .cl-sec,.compass-landing .cl-fork .cl-kicker{color:var(--dark-text)}
.compass-landing .cl-fork .cl-secsub{color:var(--muted)}
.compass-landing .cl-final{background:linear-gradient(135deg,#fff,#FCEBE5);color:var(--dark-text);text-align:center;padding:78px 0;border-top:1px solid var(--line)}
.compass-landing .cl-final h2{font-size:40px;font-weight:900;letter-spacing:-.8px;margin-bottom:14px}
.compass-landing .cl-final p{color:var(--body);font-size:18px;max-width:560px;margin:0 auto 30px}
.compass-landing .cl-footer{background:var(--panel);color:var(--muted);text-align:center;padding:26px;font-size:13px;border-top:1px solid var(--line)}
@media(max-width:900px){
  .compass-landing .cl-why-grid,.compass-landing .cl-cards{grid-template-columns:1fr}
  .compass-landing .cl-hero h1{font-size:40px}
  .compass-landing .cl-nav a{display:none}
}
`;
